<template>
  <q-page class="row">
    <div class="col-2"></div>
    <div class="col-8">
      <div class="text-h4 q-py-lg self-center">Validació catàleg</div>
      <q-spinner v-if="loading" />
      <q-toggle
        v-if="!loading"
        v-model="filtered"
        :label="filtered ? `Eliminar filtre errors (${invalidLayers.length})` : `Filtrar errors (${invalidLayers.length})`"
        left-label
      />
      <q-spinner v-if="checkingLayers.length > 0" />
      <q-tree
        v-if="!loading"
        ref="tree"
        :nodes="validatedCatalog"
        node-key="id"
        :filter="filter"
        :filter-method="filterMethod"
        default-expand-all
      >
        <template v-slot:header-root="prop">
          <div class="root">
            <span :style="'font-weight: 900; color: ' + prop.node.data.color || 'black'" class="q-mr-sm">|</span>{{ prop.node.label }}
          </div>
        </template>

        <template v-slot:header-branch="prop">
          <div class="branch">
            {{ prop.node.label }}
          </div>
        </template>

        <template v-slot:header-leaf="prop">
          <q-spinner v-if="checkingLayers.includes(prop.node.id)" />
          <q-icon v-else
            :name="validLayers.includes(prop.node.id) ? 'check' : 'close'"
            :color="validLayers.includes(prop.node.id) ? 'green-5' : 'red-9'"
          />
          <div class="q-mx-sm col-10">
            <div :class="(!checkingLayers.includes(prop.node.id) && invalidLayers.includes(prop.node.id)) ? 'text-red-9' : ''">
              {{ prop.node.label }}
              <q-btn
                flat round dense
                icon="las la-info-circle"
                size="sm"
                color="primary"
                @click="$router.push({name: 'giscube_id', params: {q: prop.node.id}})"
              >
                <q-tooltip>Obrir fitxa capa</q-tooltip>
              </q-btn>
            </div>
          </div>
        </template>

        <template v-slot:default-body="prop">
          <div class="description q-pl-lg" v-if="prop.node.data.description">
            {{ prop.node.data.description }}
          </div>
        </template>
      </q-tree>
    </div>
    <div class="col-2"></div>
  </q-page>
</template>

<script>
import { QPage, QBtn, QIcon, QSpinner, QToggle, QTree, QTooltip } from 'quasar'
import { mapState } from 'vuex'
import axios from 'axios'

import { convertLatLngToTilesGoogle, convertLatLngToTilesTMS } from 'src/lib/projections.js'

export default {
  components: {
    QPage, QBtn, QIcon, QSpinner, QToggle, QTree, QTooltip
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.q = to.params.q
      vm.$store.dispatch('catalogTree/checkCategories')
    })
  },
  data () {
    return {
      validatedCatalog: [],
      filtered: false,
      validateLayerActions: {
        wms: this.validateLayerWMS,
        tms: this.validateLayerTMS,
        geojson: this.validateLayerGeoJSON
      },
      checkingLayers: [],
      validLayers: [],
      invalidLayers: []
    }
  },
  computed: {
    ...mapState({
      catalog: state => state.catalogTree.catalog,
      loading: state => state.catalogTree.loading
    }),
    filter () {
      return this.filtered ? this.invalidLayers.join('|') : ''
    }
  },
  methods: {
    filterMethod (node, filter) {
      return filter.split('|').includes(node.id)
    },
    expandAll () {
      this.$refs.tree.expandAll()
    },
    removeLeafFromArray (array, leaf) {
      const index = array.indexOf(leaf.id)
      if (index > -1) {
        array.splice(index, 1)
      }
      return array
    },
    pushLeafToArray (array, leaf) {
      array.push(leaf.id)
      return array.filter((value, index, self) => self.indexOf(value) === index)
    },
    validLeaf (leaf) {
      this.validLayers = this.pushLeafToArray(this.validLayers, leaf)
      this.checkingLayers = this.removeLeafFromArray(this.checkingLayers, leaf)
      this.invalidLayers = this.removeLeafFromArray(this.invalidLayers, leaf)
    },
    invalidLeaf (leaf) {
      this.invalidLayers = this.pushLeafToArray(this.invalidLayers, leaf)
      this.checkingLayers = this.removeLeafFromArray(this.checkingLayers, leaf)
      this.validLayers = this.removeLeafFromArray(this.validLayers, leaf)
    },
    validateLayerWMS (leaf) {
      const config = {
        headers: this.$store.getters['auth/headers'],
        params: {
          service: 'WMS',
          request: 'GetCapabilities'
        }
      }
      this.checkingLayers = this.pushLeafToArray(this.checkingLayers, leaf)
      const url = leaf.data.children[0].url.replace('?', '')
      axios.get(url, config)
        .then(response => response.data.includes('<Layer') ? this.validLeaf(leaf) : this.invalidLeaf(leaf))
        .catch(_ => this.invalidLeaf(leaf))
    },
    validateLayerTMS (leaf) {
      const config = { headers: this.$store.getters['auth/headers'] }
      this.checkingLayers = this.pushLeafToArray(this.checkingLayers, leaf)
      const center = this.$config.home.center
      const zoom = 17
      const tiles = leaf.data.options.tms ? convertLatLngToTilesTMS(center, zoom) : convertLatLngToTilesGoogle(center, zoom)
      const url = leaf.data.children[0].url
        .replace('{z}', zoom)
        .replace('{x}', tiles.x)
        .replace('{y}', tiles.y)
      axios.get(url, config)
        .then(_ => this.validLeaf(leaf))
        .catch(_ => this.invalidLeaf(leaf))
    },
    validateLayerGeoJSON (leaf) {
      const config = { headers: this.$store.getters['auth/headers'] }
      this.checkingLayers = this.pushLeafToArray(this.checkingLayers, leaf)
      axios.get(leaf.data.children[0].url, config)
        .then(_ => this.validLeaf(leaf))
        .catch(_ => this.invalidLeaf(leaf))
    },
    checkLayer (leaf) {
      if (!leaf.data.has_children) {
        leaf.validLayer = false
      } else {
        const type = leaf.data.children[0].type.toLowerCase()
        this.validateLayerActions[type](leaf)
      }
    },
    validateNode (node) {
      if (node.header === 'branch') {
        node.children.map(child => {
          this.validateNode(child)
        })
      } else if (node.header === 'leaf') {
        this.checkLayer(node)
      }
    },
    validateCatalog () {
      this.validatedCatalog.map(node => {
        node.children.map(child => {
          this.validateNode(child)
        })
      })
    }
  },
  watch: {
    catalog: function (catalog) {
      if (this.$refs.tree && catalog) {
        this.validatedCatalog = this.catalog
        this.expandAll()
        this.validateCatalog()
      }
    }
  }
}
</script>

<style>

</style>
